import { Component, EventEmitter, Input, Output, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../../../shared/services/auth.service';

type CheckoutOption = 'login' | 'guest' | 'register';

@Component({
  selector: 'app-login-guest-step',
  standalone: false,
  templateUrl: './login-guest-step.component.html',
  styleUrl: './login-guest-step.component.scss',
})
export class LoginGuestStepComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Output() nextStepEmitter = new EventEmitter<void>();
  
  selectedOption: CheckoutOption = 'register';
  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  showPassword = false;
  isLoggingIn = false;
  loginError = '';
  showForgotPasswordForm = false;
  isSendingReset = false;
  forgotPasswordSuccess = false;
  forgotPasswordError = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    public authService: AuthService, // Make public for template access
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.initializeLoginForm();
    this.initializeForgotPasswordForm();
    
    // Check if user is already authenticated
    if (this.checkAuthenticationStatus()) {
      // User is already logged in, skip this step
      setTimeout(() => {
        this.nextStepEmitter.emit();
        this.cdr.detectChanges();
      }, 100);
    }
  }

  // Check if user is already authenticated
  checkAuthenticationStatus(): boolean {
    const isLoggedIn = this.authService.isLoggedIn();
    const currentUser = this.authService.getCurrentUser();
    
    if (isLoggedIn && currentUser) {
      return true;
    }
    
    return false;
  }

  initializeLoginForm() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });
  }

  initializeForgotPasswordForm() {
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  selectOption(option: CheckoutOption) {
    this.selectedOption = option;
    this.loginError = ''; // Clear any previous errors

    if (option === 'login') {
      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        const loginPanel = document.getElementById('checkout-login-panel');
        if (!loginPanel) return;

        const navbar = document.querySelector('app-navbar');
        const navbarBottom = Math.max(0, navbar?.getBoundingClientRect().bottom ?? 0);
        const top = loginPanel.getBoundingClientRect().top + window.scrollY - navbarBottom - 16;
        window.scrollTo({
          top,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    }
  }

  handleOptionKeydown(event: KeyboardEvent, option: CheckoutOption): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.selectOption(option);
      return;
    }

    if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.key)) {
      return;
    }

    const optionElement = event.currentTarget;
    if (!(optionElement instanceof HTMLElement)) return;

    const radioOptions = optionElement.parentElement?.querySelectorAll<HTMLElement>('[role="radio"]');
    if (!radioOptions?.length) return;

    const currentIndex = Array.from(radioOptions).indexOf(optionElement);
    if (currentIndex < 0) return;

    event.preventDefault();
    const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
    const nextIndex = (currentIndex + direction + radioOptions.length) % radioOptions.length;
    const choices: CheckoutOption[] = ['login', 'register', 'guest'];
    this.selectOption(choices[nextIndex]);
    radioOptions[nextIndex].focus();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  continueAsGuest() {
    if (this.selectedOption === 'guest') {
      this.nextStepEmitter.emit();
    }
  }

  onLoginSubmit() {
    if (this.loginForm.valid) {
      this.continueWithLogin();
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.loginForm.controls).forEach(key => {
        this.loginForm.get(key)?.markAsTouched();
      });
    }
  }

  continueWithLogin() {
    if (this.selectedOption === 'login' && this.loginForm.valid) {
      this.isLoggingIn = true;
      this.loginError = '';
      
      const loginData = {
        email: this.loginForm.get('email')?.value,
        password: this.loginForm.get('password')?.value
      };

      // Use real auth service
      this.authService.login(loginData).subscribe({
        next: (response) => {
          if (response.status === 200 && response.token && response.user) {
            // Set the session using the auth service
            try {
              this.authService.setSession(response, this.loginForm.get('rememberMe')?.value === true);
            } catch (error) {
              this.isLoggingIn = false;
              this.loginError = error instanceof Error
                ? error.message
                : 'Unable to save your sign-in session. Please check your browser settings and try again.';
              return;
            }
            this.isLoggingIn = false;
            setTimeout(() => {
              this.nextStepEmitter.emit();
              this.cdr.detectChanges();
            }, 100);
          } else {
            this.isLoggingIn = false;
            this.loginError = response.message || 'Login failed. Please try again.';
          }
        },
        error: (error) => {
          this.isLoggingIn = false;
          this.loginError = error.error?.message || 'Invalid email or password. Please try again.';
        }
      });
    }
  }

  continueWithRegistration() {
    if (this.selectedOption === 'register') {
      // Navigate to register-multi page
      this.router.navigate(['/auth/register-multi'], { 
        queryParams: { returnUrl: '/checkout' } 
      });
    }
  }

  switchToRegister() {
    this.selectOption('register');
  }

  showForgotPassword() {
    this.showForgotPasswordForm = true;
    this.forgotPasswordSuccess = false;
    this.forgotPasswordError = '';
    // Pre-fill email if user has already entered it in login form
    const loginEmail = this.loginForm.get('email')?.value;
    if (loginEmail && this.loginForm.get('email')?.valid) {
      this.forgotPasswordForm.patchValue({ email: loginEmail });
    }
  }

  hideForgotPassword() {
    this.showForgotPasswordForm = false;
    this.forgotPasswordSuccess = false;
    this.forgotPasswordError = '';
    this.forgotPasswordForm.reset();
  }

  onForgotPasswordSubmit() {
    if (this.forgotPasswordForm.valid) {
      this.isSendingReset = true;
      this.forgotPasswordError = '';
      this.forgotPasswordSuccess = false;
      
      const email = this.forgotPasswordForm.get('email')?.value;
      
      // Use real auth service for forgot password
      this.authService.forgotPassword(email).subscribe({
        next: (response) => {
          this.isSendingReset = false;
          // Check if the response indicates success
          // Most forgot password APIs return 200 status even if email doesn't exist for security reasons
          if (response.status === 200 || response.status === 201 || 
              response.message?.toLowerCase().includes('sent') || 
              response.message?.toLowerCase().includes('success') ||
              response.message?.toLowerCase().includes('email') ||
              !response.message || // Some APIs return empty message on success
              response.message === 'OK') {
            this.forgotPasswordSuccess = true;
            this.forgotPasswordError = '';
            
            // Auto-hide the success message and form after 5 seconds
            setTimeout(() => {
              this.hideForgotPassword();
            }, 5000);
          } else {
            this.forgotPasswordError = response.message || 'Failed to send reset email. Please try again.';
          }
        },
        error: (error) => {
          this.isSendingReset = false;
          this.forgotPasswordSuccess = false;
          
          // More specific error handling
          if (error.status === 404) {
            this.forgotPasswordError = 'Email address not found. Please check and try again.';
          } else if (error.status === 429) {
            this.forgotPasswordError = 'Too many requests. Please wait before trying again.';
          } else if (error.status === 0) {
            this.forgotPasswordError = 'Unable to reach the server. Check your connection and try again.';
          } else if (error.error?.message) {
            this.forgotPasswordError = error.error.message;
          } else {
            this.forgotPasswordError = 'Unable to send reset email. Please check your connection and try again.';
          }
        }
      });
    } else {
      // Mark email field as touched to show validation errors
      this.forgotPasswordForm.get('email')?.markAsTouched();
    }
  }
}
